/**
 * useDeckUndo — session-lokaler Rückgängig-Stapel des Deck-Editors (LIFO, max. 20,
 * beim Deckwechsel geleert). Gegenaktions-Prinzip, Spec
 * docs/superpowers/specs/2026-08-17-mobile-deck-editor-design.md §10:
 *   add      ↔ Menge −1 (entfernt die beim Hinzufügen entstandene Kopie)
 *   remove   ↔ Wieder-Hinzufügen in die vorherige Kategorie + Menge + Labels
 *   move     ↔ Zurückverschieben (fromCategoryId null = Typ-Fallback des Servers)
 *   quantity ↔ Menge ∓delta
 * Commander-Aktionen und Print-Wechsel registrieren keine Einträge. Registriert wird
 * ausschließlich an den Vorwärts-Callsites; die Gegenaktionen sind normale Mutationen
 * und registrieren nichts — sonst würde Strg+Z zwischen Aktion und Gegenaktion pendeln.
 * Scheitert eine Gegenaktion, meldet die Mutation den Fehler über ihr onError; der
 * Eintrag ist dann verworfen — kein Fallback, kein zweiter Toast von hier.
 * `batchMove` (Gegenaktion: ein `moveCardsBatch` mit den gemerkten Ausgangs-Kategorien) und
 * `batch` (mehrere Einzel-Einträge, deren Gegenaktionen sequenziell über `executeSingle`
 * laufen) fassen mehrere Karten in einem Undo-Eintrag zusammen. `undoEntry` liefert für diese
 * Typen `{ ok, failedCount, total }`, für Einzel-Typen weiterhin `{ ok }` — beim Busy-Guard
 * bleibt der Rückgabewert `undefined`, Aufrufer müssen das tolerieren.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export const MAX_UNDO_ENTRIES = 20;

export function useDeckUndo({ deckId, cardActions, assignLabel, getCardLabels }) {
  const stackRef = useRef([]);
  const idRef = useRef(0);
  const [size, setSize] = useState(0);
  const busyRef = useRef(false);

  // Immer die frische Fassung der Mutationen nutzen: Toast-Closures leben bis zu 8 s,
  // eingefrorene Funktions-Referenzen sähen dort einen veralteten Deck-Stand.
  const depsRef = useRef({ cardActions, assignLabel, getCardLabels });
  depsRef.current = { cardActions, assignLabel, getCardLabels };

  useEffect(() => {
    stackRef.current = [];
    setSize(0);
  }, [deckId]);

  const pushEntry = useCallback((entry) => {
    const id = ++idRef.current;
    stackRef.current = [...stackRef.current, { ...entry, id }].slice(-MAX_UNDO_ENTRIES);
    setSize(stackRef.current.length);
    return id;
  }, []);

  const registerAdd = useCallback((card) => pushEntry({
    type: 'add',
    scryfallId: card.scryfallId || card.id,
  }), [pushEntry]);

  const registerRemove = useCallback((card, previousQuantity) => pushEntry({
    type: 'remove',
    scryfallId: card.scryfallId || card.id,
    quantity: previousQuantity,
    categoryId: card.categoryId ?? null,
    labels: card.deckCardEntryId ? depsRef.current.getCardLabels(card.deckCardEntryId) : [],
  }), [pushEntry]);

  const registerMove = useCallback(({ cardId, from }) => {
    if (!from || from.isCommander) return null; // Commander-Aktionen sind keine Undo-Paare
    return pushEntry({ type: 'move', scryfallId: cardId, fromCategoryId: from.id ?? null });
  }, [pushEntry]);

  const registerQuantity = useCallback((scryfallId, delta) => pushEntry({
    type: 'quantity', scryfallId, delta,
  }), [pushEntry]);

  const registerBatchMove = useCallback((moves) => pushEntry({
    type: 'batchMove', moves,
  }), [pushEntry]);

  const registerBatch = useCallback((subEntries) => pushEntry({
    type: 'batch', entries: subEntries,
  }), [pushEntry]);

  const executeSingle = useCallback(async (payload) => {
    const { cardActions: actions, assignLabel: assign } = depsRef.current;
    switch (payload.type) {
      case 'add':
        return (await actions.handleQuantityDelta(payload.scryfallId, -1))?.ok === true;
      case 'quantity':
        return (await actions.handleQuantityDelta(payload.scryfallId, -payload.delta))?.ok === true;
      case 'move':
        return (await actions.moveCardToCategory(payload.scryfallId, payload.fromCategoryId))?.ok === true;
      case 'remove': {
        const addResult = await actions.addCardByScryfallId(
          payload.scryfallId,
          payload.categoryId != null ? { categoryId: payload.categoryId } : {},
        );
        const card = addResult?.card;
        if (!card) return false;
        if (payload.quantity > 1) {
          const result = await actions.handleQuantityDelta(payload.scryfallId, payload.quantity - 1);
          if (!result?.ok) return false;
        }
        if (card.deckCardEntryId) {
          for (const label of payload.labels) await assign(card.deckCardEntryId, label);
        }
        return true;
      }
      default:
        return false;
    }
  }, []);

  const executeUndo = useCallback(async (entry) => {
    const { cardActions: actions } = depsRef.current;
    switch (entry.type) {
      case 'batchMove': {
        const result = await actions.moveCardsBatch(
          entry.moves.map((m) => ({ scryfallId: m.scryfallId, categoryId: m.fromCategoryId })),
        );
        const failedCount = result?.failed?.length ?? entry.moves.length;
        return { ok: failedCount === 0, failedCount, total: entry.moves.length };
      }
      case 'batch': {
        let failedCount = 0;
        for (const sub of entry.entries) {
          const ok = await executeSingle(sub);
          if (!ok) failedCount += 1;
        }
        return { ok: failedCount === 0, failedCount, total: entry.entries.length };
      }
      default:
        return { ok: await executeSingle(entry) };
    }
  }, [executeSingle]);

  /** Toast-Knopf: genau diesen Eintrag; Strg+Z (entryId null): den obersten. */
  const undoEntry = useCallback(async (entryId) => {
    if (busyRef.current) return undefined;
    const stack = stackRef.current;
    const entry = entryId != null ? stack.find((e) => e.id === entryId) : stack[stack.length - 1];
    if (!entry) return undefined;
    stackRef.current = stack.filter((e) => e.id !== entry.id);
    setSize(stackRef.current.length);
    busyRef.current = true;
    try {
      return await executeUndo(entry);
    } finally {
      busyRef.current = false;
    }
  }, [executeUndo]);

  const undoLast = useCallback(() => undoEntry(null), [undoEntry]);

  return {
    registerAdd, registerRemove, registerMove, registerQuantity, registerBatchMove, registerBatch,
    undoEntry, undoLast, canUndo: size > 0,
  };
}

export default useDeckUndo;
